import React, { useState, useEffect, useRef } from "react";
import { clipFor } from "../../utils/playerClips";
import { CLIP_LOOK } from "../../data/clipLook";

/**
 * NominationFlash — the full-bleed beat when a player goes on the block.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A MOMENT, NOT A STATE. This is the whole design, and it is the reason the
 * obvious version is wrong: ON BLOCK lasts the ENTIRE bidding window, so a
 * persistent full-page backdrop is motion behind the roster grid and the entry
 * row for most of a three-hour draft — under the numbers people are bidding
 * from. As a beat it reads as broadcast; as a background it is a readability
 * problem. So it plays for FLASH_MS, fades, and hands off to the card, which is
 * the only surface that keeps the clip.
 *
 * 🔴 IT MUST NEVER BLOCK INPUT. Ben types the next price while this is on
 * screen. Three things enforce that and all three are tested:
 *   - `pointer-events: none`, so clicks land on whatever is underneath.
 *   - z-index BELOW the pinned entry bar (120), so the row he is typing into is
 *     never covered — see the z-index note below.
 *   - nothing here calls focus(), so the caret never moves.
 *
 * 🔴 THE TRIGGER IS A CHANGE, NOT AN AGE — the SOLD flash's shape exactly, and
 * for the same reasons documented on useSoldFlash: identity is a STRING so a
 * re-render cannot re-fire it, and FIRST SIGHT SEEDS AND NEVER FIRES so a
 * reload mid-draft is not treated as news. The key includes the nomination's
 * `ts`, which is what makes cancelling and re-nominating the same player fire
 * again — it is genuinely a second moment, and the room saw the first one end.
 *
 * ⚠️ Z-INDEX: "behind everything" is not literally available. The app paints an
 * opaque background above the viewport, so a layer beneath the page content
 * would be invisible and the feature would not exist. It sits above the board
 * and BELOW the entry bar (120) and every modal (200) — which is the reading
 * that matters: it never covers a control, and it never takes a click.
 *
 * ⚠️ EXPECT IT TO LOOK SOFT. These clips are ~480px wide; a 1920px page is a 4x
 * upscale. That is a property of the source, not a defect to chase.
 * ══════════════════════════════════════════════════════════════════════
 */

/**
 * The FALLBACK beat, end to end — what a clip whose duration never resolves
 * gets, which is exactly the beat that shipped.
 *
 * 🔴 DERIVED, NEVER TYPED. Every dial lives in CLIP_LOOK (src/data/clipLook.js)
 * so a tuning pass is one file.
 */
export const FLASH_MS =
  CLIP_LOOK.flashFadeInMs + CLIP_LOOK.flashHoldMs + CLIP_LOOK.flashFadeOutMs;

/**
 * The longest a beat can possibly run, and the HARD CEILING the hook's timer
 * uses. A full-bleed overlay that hangs during bidding is unrecoverable without
 * a refresh, so the outer bound does not depend on any measurement, any media
 * event, or the component getting its phases right.
 */
export const FLASH_MAX_MS =
  CLIP_LOOK.flashFadeInMs + CLIP_LOOK.flashMaxHoldMs + CLIP_LOOK.flashFadeOutMs;

/**
 * How long to hold, given what the video says about itself.
 *
 * 🔴 UNKNOWN MEANS THE SHIPPED HOLD. `duration` is NaN before metadata loads and
 * Infinity for a stream; both, and any non-positive value, fall back rather than
 * producing a beat that hangs or one that never appears.
 *
 * Clamped both ways: the floor keeps a 1s clip from making the beat a blink,
 * and the ceiling keeps one long file in a 46-file set from parking a wash over
 * the board while Ben types a price.
 */
export function holdForDuration(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return CLIP_LOOK.flashHoldMs;
  return Math.min(
    CLIP_LOOK.flashMaxHoldMs,
    Math.max(CLIP_LOOK.flashHoldMs, Math.round(n * 1000))
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 TWO PHASES, NOT ONE ANIMATION WITH A DELAY — and that is what lets the
 * hold change mid-beat.
 *
 * The shipped version was a single `animation` shorthand carrying both
 * keyframes, with the fade-out delayed by fadeIn + hold. Parameterising the
 * hold that way is impossible in practice: the duration is not known until
 * `loadedmetadata` fires, which is AFTER the entrance has started, and editing
 * the shorthand restarts every animation in the list — the fade-in would visibly
 * replay a few hundred milliseconds in.
 *
 * So the entrance and the exit are applied separately, one at a time, and the
 * switch between them is a state flip. `nomFlashIn ... forwards` leaves the
 * layer at opacity 1 and `nomFlashOut` starts from 1, so the handover is
 * seamless. Changing the hold now only reschedules a setTimeout; it never
 * touches the running animation.
 *
 * 🔴 STILL NO CSS EDIT. Both keyframes already exist and neither changed —
 * auction.css:53-57 says the SHAPE lives there and every DURATION lives in
 * CLIP_LOOK, and this is that split being used rather than bypassed.
 * ══════════════════════════════════════════════════════════════════════
 */
const FLASH_IN = `nomFlashIn ${CLIP_LOOK.flashFadeInMs}ms ease-out forwards`;
const FLASH_OUT = `nomFlashOut ${CLIP_LOOK.flashFadeOutMs}ms ease-in forwards`;

/**
 * Whether this device has asked for less motion. Absent matchMedia = allowed.
 * Exported so /rate's hero card answers the question the same way rather than
 * carrying a second copy of the media query.
 */
export function prefersReducedMotion() {
  try {
    return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  } catch {
    return false;
  }
}

/**
 * The nomination, for as long as it is NEWS.
 *
 * Deliberately the same code shape as useSoldFlash (NominationBlock.jsx) rather
 * than a clever shared abstraction: the two differ in what counts as a change
 * (an append there, a new `ts` here) and collapsing them would hide exactly the
 * part worth reading.
 *
 * 🔴 `active` SEEDS BUT DOES NOT FIRE. When the kill switch is off this hook
 * still tracks every nomination — it just never flashes one. That is what stops
 * turning CLIPS ON mid-draft from immediately washing the board with a 2.5s
 * beat for a player who went up ten minutes ago: the switch is a setting, not a
 * nomination. Seeding regardless is what keeps the key current, so the NEXT
 * player up flashes normally.
 */
export function useNominationFlash(board, ms = FLASH_MS, active = true) {
  const nomination = board?.nomination || null;
  // `ts` is what separates a re-nomination from a re-render.
  const key = nomination ? `${nomination.name}|${nomination.pos}|${nomination.ts}` : "";

  const [flash, setFlash] = useState(null);
  const nowRef = useRef(null);
  nowRef.current = { key, nomination, active };
  const seenRef = useRef(null);

  useEffect(() => {
    const cur = nowRef.current;
    const prev = seenRef.current;
    seenRef.current = cur.key;

    if (prev === null) return undefined;          // first sight: seed only
    if (cur.key === prev) return undefined;       // same nomination, new object
    if (!cur.nomination) {                        // passed, or the block cleared
      setFlash(null);
      return undefined;
    }
    if (!cur.active) return undefined;            // switch off: seeded, not fired
    setFlash(cur.nomination);
    const t = setTimeout(() => setFlash(null), ms);
    return () => clearTimeout(t);
  }, [key, ms]);

  return flash;
}

/**
 * `enabled` is the SAME kill switch the card backdrop uses (ClipToggle) — one
 * toggle for the feature, not one per surface. Off, the hook still watches the
 * block but never fires, so no timer is armed and this renders null.
 */
export default function NominationFlash({ board, enabled = false }) {
  // 🔴 THE CEILING, NOT THE BEAT. The hook clears its own state at the worst
  // case; the phases below end the visible beat well before that in every
  // normal case. Two timers with different jobs — one is the feature, one is
  // the guarantee that a full-bleed overlay cannot hang during bidding.
  const flash = useNominationFlash(board, FLASH_MAX_MS, enabled);
  const clip = flash ? clipFor(flash.name) : null;
  const [failed, setFailed] = useState(false);

  /**
   * `ts` is in the key, not just the name: cancelling and re-nominating the
   * same player is genuinely a second moment and has to reset the phases. The
   * URL alone would not change.
   */
  const beatKey = flash ? `${flash.name}|${flash.ts}` : "";

  // "in" -> "out" -> "done". `done` renders null, which is what actually takes
  // the layer off screen; the hook's ceiling is only the backstop.
  const [phase, setPhase] = useState("in");
  const [holdMs, setHoldMs] = useState(CLIP_LOOK.flashHoldMs);
  const startedRef = useRef(0);

  // A new beat: back to the shipped fallback until this clip's own metadata
  // says otherwise, and a fresh start time to schedule against.
  useEffect(() => {
    setFailed(false);
    setPhase("in");
    setHoldMs(CLIP_LOOK.flashHoldMs);
    startedRef.current = Date.now();
  }, [beatKey]);

  /**
   * 🔴 SCHEDULED AGAINST THE START, NOT AGAINST NOW. `loadedmetadata` lands
   * partway through the entrance, so a fresh `setTimeout(fadeIn + hold)` at
   * that moment would add however long the metadata took to the beat. Measuring
   * elapsed and scheduling the REMAINDER keeps the total honest, and makes the
   * timer idempotent if the duration is reported twice (which `durationchange`
   * does on some browsers).
   */
  useEffect(() => {
    if (!beatKey || phase !== "in") return undefined;
    const elapsed = Date.now() - startedRef.current;
    const remaining = Math.max(0, CLIP_LOOK.flashFadeInMs + holdMs - elapsed);
    const t = setTimeout(() => setPhase("out"), remaining);
    return () => clearTimeout(t);
  }, [beatKey, holdMs, phase]);

  useEffect(() => {
    if (!beatKey || phase !== "out") return undefined;
    const t = setTimeout(() => setPhase("done"), CLIP_LOOK.flashFadeOutMs);
    return () => clearTimeout(t);
  }, [beatKey, phase]);

  /**
   * The measurement. Both events, because `loadedmetadata` is the normal path
   * and `durationchange` is how some browsers correct an initial estimate.
   * A duration that never resolves simply never calls this, and the fallback
   * hold set above is what runs — the shipped beat, exactly.
   *
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 TOO LATE IS DISCARDED. A slow phone on a 3.2 MB clip can report its
   * duration after the fade-out has already begun, and by then the answer is
   * useless: the hold is over, and the only thing left to do with a new number
   * would be to disturb an exit that is already playing.
   *
   * It is inert today for two reasons and NEITHER OF THEM IS A DECISION — the
   * phase-1 effect early-returns once `phase !== "in"`, and the animation string
   * is a module constant so a re-render during "out" produces the identical
   * value and React's style diff never rewrites the property. Both are true by
   * accident of how this happens to be written. Adding `holdMs` to the exit
   * effect's deps, or interpolating the hold into the animation string, would
   * restart the fade-out or snap the layer back to opaque — the visible replay
   * the two-phase split exists to prevent, arriving at the other boundary.
   *
   * So the guard is explicit and the ref is what makes it correct: this handler
   * is recreated each render, but a media event can fire against a closure from
   * before the phase flipped.
   * ══════════════════════════════════════════════════════════════════════
   */
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const readDuration = (e) => {
    if (phaseRef.current !== "in") return;
    const next = holdForDuration(e?.currentTarget?.duration);
    setHoldMs(prev => (prev === next ? prev : next));
  };

  if (!clip || failed || phase === "done" || prefersReducedMotion()) return null;

  return (
    <div
      data-testid="nomination-flash"
      data-phase={phase}
      aria-hidden="true"
      style={{
        // 🔴 FIXED AND pointer-events: none, AND THAT IS NOW LOAD-BEARING TWICE.
        // It was always about never covering the entry row or taking a click.
        // Since the nomination-height reserve it is also what keeps this out of
        // the layout entirely: an element whose duration can affect `gridTop`
        // would move the board for as long as a clip happens to run, which is
        // the exact swing the reserve exists to close.
        position: "fixed", inset: 0, zIndex: 100,
        pointerEvents: "none", overflow: "hidden",
        animation: phase === "out" ? FLASH_OUT : FLASH_IN,
      }}
    >
      {/* The intensity, in one place. The layer above animates 0 -> 1 -> 0, so
          everything inside here scales together and `flashOpacity` stays a
          single honest dial rather than one of three interacting numbers. */}
      <div
        data-testid="flash-wash"
        style={{ position: "absolute", inset: 0, opacity: CLIP_LOOK.flashOpacity }}
      >
        <video
          // 🔴 KEYED ON THE BEAT, NOT THE FILE. Normally the whole layer
          // unmounts between beats, so this rarely matters — but cancelling and
          // re-nominating the SAME player MID-BEAT keeps the tree mounted at an
          // unchanged url, which would reuse an element already part-way
          // through (and, with `loop` now gone, possibly already ended). A beat
          // is a moment; its element is per-moment. The file is cached, so a
          // fresh element costs nothing and re-fires loadedmetadata, which is
          // what the hold reset above already assumes.
          key={beatKey}
          data-testid="flash-video"
          src={clip.url}
          ref={(el) => { if (el) el.muted = true; }}
          // 🔴 NO `loop`. The beat now runs as long as the clip does, so looping
          // would mean a second pass that gets cut off part-way through —
          // visibly interrupted, which is worse than the short beat this
          // replaces. A clip below the floor freezes on its last frame for the
          // remainder instead, which at 1s is 400ms and reads as a held shot.
          autoPlay muted playsInline preload="none"
          onLoadedMetadata={readDuration}
          onDurationChange={readDuration}
          onError={() => setFailed(true)}
          style={{
            width: "100%", height: "100%", display: "block",
            // 🔴 CONTAIN, NOT COVER. Cover scaled a ~480px source to fill 1920
            // and the grain was the first thing anyone noticed. Contain
            // letterboxes it at its own aspect: nothing cropped, nothing
            // magnified past its natural size.
            objectFit: "contain",
            filter: `blur(${CLIP_LOOK.flashBlurPx}px)`,
            // The same per-clip framing the card uses.
            ...(clip.focus ? { objectPosition: clip.focus } : null),
          }}
        />
        {/* Sits the footage IN the page rather than on top of it. */}
        <div
          data-testid="flash-vignette"
          style={{ position: "absolute", inset: 0, background: CLIP_LOOK.flashVignette }}
        />
      </div>
    </div>
  );
}
